import React from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { t, useLanguage } from '../useTranslations';
import './BreadcrumbNav.css';

/**
 * Route configuration mapping paths to module names and tab translation keys.
 * defaultTab: the tab shown when no ?tab= param is present (no tab crumb shown).
 */
const ROUTE_CONFIG = {
  '/dashboard': {
    moduleKey: 'sidebar.dashboard',
    defaultTab: null,
    tabs: {
      overview: 'modules.dashboard.tabOverview',
      kri: 'modules.dashboard.tabKRI',
      koncern: 'modules.dashboard.tabKoncern',
    },
  },
  '/geographical-risk': {
    moduleKey: 'sidebar.geographicalRisk',
    defaultTab: 'overview',
    tabs: {
      overview: 'modules.geoRisk.tabs.overview',
      'risk-management': 'modules.geoRisk.tabs.riskManagement',
      countries: 'modules.geoRisk.tabs.countryManager',
      'model-config': 'modules.geoRisk.tabs.modelConfiguration',
      validation: 'modules.geoRisk.tabs.validation',
      'map-view': 'modules.geoRisk.tabs.mapView',
      'ab-test': 'modules.geoRisk.tabs.abTest',
      'country-history': 'modules.geoRisk.tabs.countryHistory',
    },
  },
  '/customer-risk': {
    moduleKey: 'sidebar.customerRisk',
    defaultTab: 'model-builder',
    tabs: {
      'model-builder': 'modules.customerRisk.modelbuilder',
      simulation: 'modules.customerRisk.simulation',
      parameters: 'modules.customerRisk.parameter',
      validation: 'modules.customerRisk.validation',
      comparison: 'modules.customerRisk.comparison.comparisonTab',
    },
  },
  '/bwra': {
    moduleKey: 'sidebar.bwra',
    defaultTab: 'overview',
    tabs: {
      overview: 'modules.bwra.overview',
      scope: 'modules.bwra.scope.tab',
      appetite: 'modules.bwra.appetite.tab',
      register: 'modules.bwra.riskRegister',
      controls: 'modules.bwra.controlsTab',
      policies: 'modules.bwra.policies.tab',
      mapping: 'modules.bwra.modelMapping',
      compliance: 'modules.bwra.compliance.tab',
      orgreport: 'modules.bwra.orgReport.tab',
      actionplan: 'modules.bwra.actionPlan.tab',
      annualreview: 'modules.bwra.annualReview.tab',
      heatmap: 'modules.bwra.heatmapTab',
      gapanalysis: 'modules.bwra.gapAnalysis.tab',
      boardreport: 'modules.bwra.boardReport.tab',
      trendanalysis: 'modules.bwra.trendAnalysis.tab',
    },
  },
  '/transaction-monitoring': {
    moduleKey: 'sidebar.transactionMonitoring',
    defaultTab: 'overview',
    tabs: {
      overview: 'modules.transactionMonitoring.tabOverview',
      rules: 'modules.transactionMonitoring.tabRules',
      mapping: 'modules.transactionMonitoring.tabMapping',
      performance: 'modules.transactionMonitoring.tabPerformance',
      modeloverview: 'modules.transactionMonitoring.tabModelOverview',
      thresholds: 'modules.transactionMonitoring.tabThresholds',
      validation: 'modules.transactionMonitoring.tabValidation',
      alerts: 'modules.transactionMonitoring.tabAlerts',
      templates: 'modules.transactionMonitoring.tabTemplates',
      scenarios: 'modules.transactionMonitoring.tabScenarioTest',
    },
  },
  '/periodic-reporting': {
    moduleKey: 'sidebar.periodicReporting',
    defaultTab: 'annual',
    tabs: {
      annual: 'modules.periodicReporting.tabAnnual',
      comparison: 'modules.periodicReporting.tabComparison',
      export: 'modules.periodicReporting.tabExport',
      schedule: 'modules.periodicReporting.tabSchedule',
    },
  },
  '/admin': {
    moduleKey: 'sidebar.admin',
    defaultTab: 'overview',
    tabs: {
      overview: 'modules.administration.tabs.overview',
      audit: 'modules.administration.tabs.audit',
      users: 'modules.administration.tabs.users',
      entities: 'modules.administration.tabs.entities',
      settings: 'modules.administration.tabs.settings',
      licenses: 'modules.administration.tabs.licenses',
      validation: 'modules.administration.tabs.validation',
      activity: 'modules.administration.tabs.activity',
      export: 'modules.administration.tabs.export',
      approval: 'modules.administration.tabs.approval',
      distribution: 'modules.administration.tabs.distribution',
      manuals: 'modules.administration.tabs.manuals',
      backup: 'modules.administration.tabs.backup',
      systemHealth: 'modules.administration.tabs.systemHealth',
      reportTemplates: 'modules.administration.tabs.reportTemplates',
    },
  },
};

const BreadcrumbNav = () => {
  useLanguage();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const config = ROUTE_CONFIG[location.pathname];
  if (!config) return null;

  const tab = searchParams.get('tab');
  const isOnDefaultTab = !tab || tab === config.defaultTab;
  const isHome = location.pathname === '/dashboard';

  // Build breadcrumb items
  const crumbs = [];

  if (!isHome) {
    crumbs.push({
      label: t('common.breadcrumb.home'),
      isClickable: true,
      onClick: () => navigate('/'),
    });
  }

  const moduleIsLast = isOnDefaultTab;
  crumbs.push({
    label: t(config.moduleKey),
    isClickable: !isHome && !moduleIsLast,
    onClick: !isHome && !moduleIsLast ? () => navigate({ pathname: location.pathname, search: '' }) : null,
  });

  if (!isOnDefaultTab && tab && config.tabs[tab]) {
    crumbs.push({
      label: t(config.tabs[tab]),
      isClickable: false,
      onClick: null,
    });
  }

  // Skip rendering if only a single non-clickable crumb (e.g. Dashboard with no tab)
  if (crumbs.length === 1 && !crumbs[0].isClickable) return null;

  return (
    <nav className="breadcrumb-nav" aria-label={t('common.breadcrumb.ariaLabel')}>
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <span className="breadcrumb-sep" aria-hidden="true">›</span>
            )}
            {crumb.isClickable ? (
              <button
                className="breadcrumb-item breadcrumb-item--link"
                onClick={crumb.onClick}
                type="button"
              >
                {crumb.label}
              </button>
            ) : (
              <span className={`breadcrumb-item${isLast ? ' breadcrumb-item--current' : ''}`}>
                {crumb.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default BreadcrumbNav;
